// §11.12 — App Inspector.
//
// "Installed manifests with validation results; live bridge message log (channel, direction, type,
// size, dropped-with-reason); granted permissions per app; a manifest validator that accepts a
// pasted URL or JSON. **This is the tool that makes wrapping app #5 an afternoon.**"
//
// So it is built for the person debugging, not for the screenshot: the log is live and shows the
// refused frames next to the ones that went through, because a message that silently did not
// happen is the hardest kind to chase.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  HangarAPI,
  InspectorApp,
  InspectorFrame,
  InspectorShellAccess,
  InspectorValidation,
  InspectorWindowStat,
} from '@hangar/shared';
import { INSPECTOR_SAMPLE_MS, INSPECTOR_SPARK_SAMPLES } from '@/config';

type Tab = 'apps' | 'bridge' | 'validate' | 'perf';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'apps', label: 'Apps' },
  { id: 'bridge', label: 'Bridge' },
  // §34.3 — the readout §11.12 promised (R5). Last, because it is the one tab that samples.
  { id: 'perf', label: 'Performance' },
  { id: 'validate', label: 'Validate' },
];

function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} kB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function clockOf(at: number): string {
  const date = new Date(at);
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(
    date.getMilliseconds(),
    3,
  )}`;
}

function AppCard({ app }: { app: InspectorApp }) {
  const [raw, setRaw] = useState(false);
  return (
    <section className="ins-app" data-testid={`ins-app-${app.id}`}>
      <header className="ins-app-head">
        <h3>{app.name}</h3>
        <span className="ins-version">{app.version}</span>
        <span
          className="ins-health"
          data-health={app.health}
          data-testid={`ins-health-${app.id}`}
          title={`${app.failures} consecutive failed health checks`}
        >
          {app.health === 'ok' ? 'reachable' : 'not responding'}
        </span>
      </header>

      <dl className="ins-facts">
        <dt>Origin</dt>
        <dd data-testid={`ins-origin-${app.id}`}>{app.origin}</dd>
        <dt>Entry</dt>
        <dd>{app.entry}</dd>
      </dl>

      {/* §9.2 — re-validated now, not trusted because it passed at install time. */}
      <p className="ins-validation" data-testid={`ins-validation-${app.id}`}>
        {app.problems.length === 0 ? (
          <span className="ins-ok">Manifest valid</span>
        ) : (
          <span className="ins-bad">{app.problems.length} manifest problem(s)</span>
        )}
      </p>
      {app.problems.length > 0 && (
        <ul className="ins-problems">
          {app.problems.map((problem) => (
            <li key={`${problem.path}:${problem.message}`}>
              <code>{problem.path}</code> {problem.message}
            </li>
          ))}
        </ul>
      )}

      {/* §9.4 — what it holds, and what it asked for and did not get. The difference is the point. */}
      <div className="ins-perms" data-testid={`ins-perms-${app.id}`}>
        {app.granted.map((permission) => (
          <span key={permission} className="ins-perm" data-granted="true">
            {permission}
          </span>
        ))}
        {app.ungranted.map((permission) => (
          <span key={permission} className="ins-perm" data-granted="false">
            {permission}
          </span>
        ))}
        {app.granted.length === 0 && app.ungranted.length === 0 && (
          <span className="ins-none">no permissions requested</span>
        )}
      </div>

      {app.instances.length > 0 && (
        <table className="ins-instances" data-testid={`ins-instances-${app.id}`}>
          <thead>
            <tr>
              <th>Window</th>
              <th>Bridge</th>
              <th>Drops</th>
              <th>Picker grants (§9.5)</th>
            </tr>
          </thead>
          <tbody>
            {app.instances.map((instance) => (
              <tr key={instance.instanceKey}>
                <td>{instance.winId}</td>
                <td data-testid={`ins-state-${instance.instanceKey}`}>{instance.bridge}</td>
                <td>{instance.drops}</td>
                <td>{instance.grants.length === 0 ? '—' : instance.grants.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button type="button" className="ins-raw-toggle" onClick={() => setRaw((was) => !was)}>
        {raw ? 'Hide manifest' : 'Show manifest'}
      </button>
      {raw && (
        <pre className="ins-raw" data-testid={`ins-raw-${app.id}`}>
          {JSON.stringify(app.manifest, null, 2)}
        </pre>
      )}
    </section>
  );
}

function BridgeLog({ frames }: { frames: readonly InspectorFrame[] }) {
  const [onlyDropped, setOnlyDropped] = useState(false);
  const shown = onlyDropped ? frames.filter((frame) => frame.dropped) : frames;

  return (
    <div className="ins-bridge">
      <label className="ins-filter">
        <input
          type="checkbox"
          data-testid="ins-only-dropped"
          checked={onlyDropped}
          onChange={(event) => setOnlyDropped(event.target.checked)}
        />
        Dropped only
      </label>

      <table className="ins-log" data-testid="ins-log">
        <thead>
          <tr>
            <th>Time</th>
            <th>App</th>
            <th>Dir</th>
            <th>Channel</th>
            <th>Type</th>
            <th>Size</th>
            <th>Dropped — why</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((frame, index) => (
            <tr
              key={`${frame.at}:${index}`}
              data-dropped={frame.dropped ? 'true' : 'false'}
              data-testid={frame.dropped ? 'ins-frame-dropped' : 'ins-frame'}
            >
              <td>{clockOf(frame.at)}</td>
              <td>{frame.appId ?? '—'}</td>
              <td>{frame.direction === 'in' ? 'app→shell' : 'shell→app'}</td>
              <td data-testid="ins-frame-channel">{frame.channel}</td>
              <td data-testid="ins-frame-type">{frame.type}</td>
              <td>{frame.size === 0 ? '—' : bytes(frame.size)}</td>
              <td data-testid="ins-frame-reason">
                {frame.dropped ? `${frame.dropped.reason} — ${frame.dropped.detail}` : ''}
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr>
              <td colSpan={7} className="ins-none" data-testid="ins-log-empty">
                No frames yet. Launch an installed app.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Validator({ access }: { access: InspectorShellAccess }) {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InspectorValidation | null>(null);

  const run = useCallback(() => {
    setBusy(true);
    void access
      .validate(input)
      .then(setResult)
      .finally(() => setBusy(false));
  }, [access, input]);

  return (
    <div className="ins-validate">
      <p className="ins-hint">A manifest URL, an origin, or the JSON itself.</p>
      <textarea
        data-testid="ins-validate-input"
        spellCheck={false}
        value={input}
        placeholder="https://app.example.com  —  or  {&quot;id&quot;: …}"
        onChange={(event) => setInput(event.target.value)}
      />
      <button type="button" data-testid="ins-validate-run" onClick={run} disabled={busy}>
        {busy ? 'Checking…' : 'Validate'}
      </button>

      {result && (
        <div className="ins-result" data-testid="ins-validate-result" data-ok={String(result.ok)}>
          <p>
            {result.ok ? (
              <span className="ins-ok">Valid manifest</span>
            ) : (
              <span className="ins-bad">{result.problems.length} problem(s)</span>
            )}
            {result.fetchedFrom && <span className="ins-from"> · checked against {result.fetchedFrom}</span>}
          </p>
          {/* The zod path is the whole value of this: "which field, and what about it". */}
          <ul className="ins-problems" data-testid="ins-validate-problems">
            {result.problems.map((problem) => (
              <li key={`${problem.path}:${problem.message}`}>
                <code data-testid="ins-problem-path">{problem.path}</code>{' '}
                <span data-testid="ins-problem-message">{problem.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * §34.3 (M57.4) — the Performance tab, honest about what a browser can attribute (R5 discharged).
 *
 * The promise §11.12 made was a "memory readout", and the reason it waited this long is that the
 * honest version of it is smaller than the one people imagine: **a browser cannot attribute heap to
 * a window**. There is no per-iframe, per-component or per-app memory figure available to page
 * script in any engine. `performance.memory` is one number for the whole JS heap of the document,
 * Chromium-only, and quantised — so this tab shows it as *one* global sparkline and says which
 * engine it came from, rather than dividing it by window count and inventing per-app figures.
 *
 * What *is* attributable is counted per window instead: DOM nodes under its `.win-host`, and the
 * canvases inside it. Those two explain most of what makes a desktop feel heavy, and the Arcade's
 * "0 frames while backgrounded" budget (§24.6) finally has a surface a person can watch.
 *
 * Sampling runs on a 2 s interval and only while this component is mounted — the tab is rendered
 * only when it is selected, so "zero cost otherwise" is structural rather than a flag to remember.
 */
function Performance({ access }: { access: InspectorShellAccess }) {
  const [stats, setStats] = useState<InspectorWindowStat[]>([]);
  const [heap, setHeap] = useState<number[]>([]);
  const [fps, setFps] = useState<number | null>(null);

  // The heap reading, or `null` on an engine that does not offer one. Read through a cast because
  // `performance.memory` is not in the DOM lib: it is a Chromium extension, which is exactly what
  // the label below tells the user.
  const heapNow = useCallback((): number | null => {
    const memory = (globalThis.performance as { memory?: { usedJSHeapSize?: number } } | undefined)
      ?.memory;
    return typeof memory?.usedJSHeapSize === 'number' ? memory.usedJSHeapSize : null;
  }, []);

  useEffect(() => {
    const take = (): void => {
      setStats(access.windows());
      const used = heapNow();
      if (used !== null) {
        // A fixed-length window of samples: a sparkline that grew forever would re-scale itself
        // every tick and stop being comparable with the minute before it.
        setHeap((current) => [...current, used].slice(-INSPECTOR_SPARK_SAMPLES));
      }
    };
    take();
    const timer = setInterval(take, INSPECTOR_SAMPLE_MS);
    return () => clearInterval(timer);
  }, [access, heapNow]);

  /**
   * Global rAF ticks per second, measured in the shell's own document — which is the app's document
   * too, since a built-in runs in-process (§9.1). No capability needed, and no shell involvement: it
   * is the same frame clock everything on the desktop shares.
   */
  useEffect(() => {
    let frames = 0;
    let handle = 0;
    let mounted = true;
    const tick = (): void => {
      frames += 1;
      handle = requestAnimationFrame(tick);
    };
    handle = requestAnimationFrame(tick);
    const timer = setInterval(() => {
      if (!mounted) return;
      setFps(Math.round((frames * 1000) / INSPECTOR_SAMPLE_MS));
      frames = 0;
    }, INSPECTOR_SAMPLE_MS);
    return () => {
      mounted = false;
      cancelAnimationFrame(handle);
      clearInterval(timer);
    };
  }, []);

  const spark = useMemo(() => sparkPath(heap), [heap]);
  const latest = heap[heap.length - 1];

  return (
    <div className="ins-perf" data-testid="ins-perf">
      <section className="ins-perf-global">
        <div className="ins-perf-metric" data-testid="ins-perf-heap">
          <span className="ins-perf-label">JS heap</span>
          {latest === undefined ? (
            // §34.3 — labelled, not hidden: an empty box would read as "no memory used".
            <span className="ins-perf-none" data-testid="ins-perf-heap-unavailable">
              Chromium only
            </span>
          ) : (
            <>
              <span className="ins-perf-value">{bytes(latest)}</span>
              {/* Drawn by code, like every visual in this wave (D-043/D-497). One path, no library. */}
              <svg
                className="ins-perf-spark"
                viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                data-testid="ins-perf-spark"
              >
                <path d={spark} fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </>
          )}
        </div>

        <div className="ins-perf-metric" data-testid="ins-perf-frames">
          <span className="ins-perf-label">Frames</span>
          <span className="ins-perf-value">{fps === null ? '—' : `${fps}/s`}</span>
        </div>

        <div className="ins-perf-metric" data-testid="ins-perf-windows">
          <span className="ins-perf-label">Windows</span>
          <span className="ins-perf-value">{stats.length}</span>
        </div>
      </section>

      <table className="ins-perf-table" data-testid="ins-perf-table">
        <thead>
          <tr>
            <th>App</th>
            <th>Window</th>
            <th>DOM nodes</th>
            <th>Canvases</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {stats.map((stat) => (
            <tr key={stat.winId} data-testid={`ins-perf-row-${stat.winId}`}>
              <td>{stat.appName}</td>
              <td>{stat.title}</td>
              <td data-testid={`ins-perf-nodes-${stat.winId}`}>
                {stat.nodes === null ? (
                  <span className="ins-perf-none">external frame</span>
                ) : (
                  stat.nodes
                )}
              </td>
              <td>{stat.canvases}</td>
              <td>
                <button
                  type="button"
                  data-testid={`ins-perf-close-${stat.winId}`}
                  onClick={() => access.closeWindow(stat.winId)}
                >
                  Close window
                </button>
              </td>
            </tr>
          ))}
          {stats.length === 0 && (
            <tr>
              <td colSpan={5} className="ins-perf-none" data-testid="ins-perf-empty">
                No windows open.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const SPARK_W = 120;
const SPARK_H = 28;

/**
 * The sparkline, as one SVG path.
 *
 * Scaled to the **observed** range rather than to the heap limit: a desktop using 40 MB of a 4 GB
 * limit would otherwise draw a flat line along the bottom and show nothing at all. A single sample
 * draws a flat line by definition, which is honest — one reading has no shape.
 */
function sparkPath(samples: readonly number[]): string {
  if (samples.length === 0) return '';
  const min = Math.min(...samples);
  const max = Math.max(...samples);
  const span = max - min || 1;
  const step = samples.length === 1 ? 0 : SPARK_W / (samples.length - 1);
  return samples
    .map((value, index) => {
      const x = (index * step).toFixed(1);
      const y = (SPARK_H - ((value - min) / span) * SPARK_H).toFixed(1);
      return `${index === 0 ? 'M' : 'L'}${x} ${y}`;
    })
    .join(' ');
}

export function Inspector({ access }: { api: HangarAPI; access: InspectorShellAccess }) {
  const [tab, setTab] = useState<Tab>('apps');
  const [tick, setTick] = useState(0);

  // Live, because §11.12 says "live": every frame and every drop re-reads the log.
  useEffect(() => access.subscribe(() => setTick((n) => n + 1)), [access]);

  const apps = useMemo(() => {
    void tick;
    return access.apps();
  }, [access, tick]);
  const frames = useMemo(() => {
    void tick;
    return access.frames();
  }, [access, tick]);

  return (
    <div className="inspector" data-testid="inspector">
      <nav className="ins-tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            data-testid={`ins-tab-${entry.id}`}
            data-active={String(tab === entry.id)}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
            {entry.id === 'bridge' && frames.length > 0 && (
              <span className="ins-count" data-testid="ins-frame-count">
                {frames.length}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="ins-body" role="tabpanel">
        {tab === 'apps' &&
          (apps.length === 0 ? (
            <p className="ins-none" data-testid="ins-no-apps">
              No apps installed yet.
            </p>
          ) : (
            apps.map((app) => <AppCard key={app.id} app={app} />)
          ))}
        {tab === 'bridge' && <BridgeLog frames={frames} />}
        {/* Mounted only while selected, which is what makes §34.3's "zero cost otherwise" true. */}
        {tab === 'perf' && <Performance access={access} />}
        {tab === 'validate' && <Validator access={access} />}
      </div>
    </div>
  );
}
